import {useContext, useState} from 'react';

import {ErrorMessage, Field} from 'formik';
import {useTranslation} from 'react-i18next';

import LanguageIcon from '@mui/icons-material/Language';
import PlaceIcon from '@mui/icons-material/Place';
import {Box, Grid, Typography} from '@mui/material';

import OnboardingSection from '../OnboardingSection';

import {renderCountrySelectField} from './JobSearchPreferencesUtils';
import {EmpionSelectField} from '../../../../../components/EmpionSelectField/EmpionSelectField';
import {EmpionTextField} from '../../../../../components/EmpionTextField/EmpionTextField';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {BIG_CITIES_MAPPING, PROXIMITY_SEARCH_KM_OPTIONS} from '../../../../../lib/constants';

interface JobSearchPreferencesProps {
  formik: any;
}

export default function JobSearchPreferences({formik}: JobSearchPreferencesProps) {
  const {t} = useTranslation();

  const lang = useContext(LanguageContext);
  const [showProximitySearch, setShowProximitySearch] = useState(false);
  const countryHasBigCitiesMapped = (country: any) => {
    return country && BIG_CITIES_MAPPING[country?.isoCode]?.length > 0;
  };

  const shouldRenderBigCities = () => {
    return (
      formik.values.applicantOnboarding.jobSearchPreferences.locationType === 'specific' &&
      countryHasBigCitiesMapped(formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.country)
    );
  };

  const getBigCitiesOptions = (isoCode: string) => {
    const bgCities = BIG_CITIES_MAPPING[isoCode] || [];
    return [...bgCities, {id: 'others', label: t('shared.other')}];
  };

  const handleCitySelection = (city: any) => {
    if (city.id === 'others') {
      setShowProximitySearch(!showProximitySearch);
    }
    let selectedCities: any[] = formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities;

    if (selectedCities.some((entry) => entry === city.id)) {
      selectedCities = selectedCities.filter((entry) => entry !== city.id);
    } else {
      selectedCities = [...selectedCities, city.id];
    }

    formik.setFieldValue('applicantOnboarding.jobSearchPreferences.preferredLocation.cities', selectedCities);
  };

  const getHoverBackgroundColor = () => {
    return orangeColors[4];
  };

  return (
    <OnboardingSection title={t('applicantOnboarding.jobSearchPreferences.title')}>
      <Grid container id='jobSearchPreferences'>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mb: 5,
          }}
        >
          <Box
            sx={{
              borderRadius: '16px',
              backgroundColor:
                formik.values.applicantOnboarding.jobSearchPreferences.locationType === 'anywhere'
                  ? getHoverBackgroundColor()
                  : 'background.default',
              p: 1,
              width: {
                xs: '50%',
                md: '25%',
              },
              textAlign: 'center',
              '&:hover': {
                backgroundColor: getHoverBackgroundColor(),
                cursor: 'pointer',
              },
            }}
            onClick={() => {
              formik.setFieldValue('applicantOnboarding.jobSearchPreferences.locationType', 'anywhere');
            }}
          >
            <LanguageIcon
              sx={{
                fontSize: '5rem',
              }}
            />
            <Typography variant='h6'>{t('applicantOnboarding.jobSearchPreferences.locationOptionRemote')}</Typography>
          </Box>
          <Box
            sx={{
              borderRadius: '16px',
              backgroundColor:
                formik.values.applicantOnboarding.jobSearchPreferences.locationType === 'specific'
                  ? getHoverBackgroundColor()
                  : 'background.default',
              p: 1,
              width: {
                xs: '50%',
                md: '25%',
              },
              textAlign: 'center',
              '&:hover': {
                backgroundColor: getHoverBackgroundColor(),
                cursor: 'pointer',
              },
            }}
            onClick={() => {
              formik.setFieldValue('applicantOnboarding.jobSearchPreferences.locationType', 'specific');
            }}
          >
            <PlaceIcon
              sx={{
                fontSize: '5rem',
              }}
            />
            <Typography variant='h6'>{t('applicantOnboarding.jobSearchPreferences.locationOptionSpecific')}</Typography>
          </Box>
        </Box>
        <Typography variant='body2' color='error'>
          <ErrorMessage name='applicantOnboarding.jobSearchPreferences.locationType' component={'span'} />
        </Typography>

        <Grid item xs={12} md={12}>
          {renderCountrySelectField(formik, t, lang)}
        </Grid>

        {shouldRenderBigCities() && (
          <Grid item xs={12} md={12}>
            <Typography variant='h4' mt={3}>
              {t('applicantOnboarding.jobSearchPreferences.formLabels.cities')}
            </Typography>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: {
                  xs: 1,
                  md: 2,
                },
                mb: 5,
                mt: 3,
              }}
            >
              {getBigCitiesOptions(
                formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.country.isoCode
              ).map((city: any) => (
                <Box
                  key={city.id}
                  sx={{
                    borderRadius: '16px',
                    backgroundColor:
                      formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities.includes(
                        city.id
                      ) ||
                      (city.id === 'others' && showProximitySearch)
                        ? getHoverBackgroundColor()
                        : 'background.default',
                    p: 3,
                    width: {
                      xs: '40%',
                      md: '12.5%',
                    },
                    textAlign: 'center',
                    '&:hover': {
                      backgroundColor: getHoverBackgroundColor(),
                      cursor: 'pointer',
                    },
                  }}
                  onClick={handleCitySelection.bind(null, city)}
                >
                  <PlaceIcon
                    sx={{
                      fontSize: '3rem',
                    }}
                  />
                  <Typography variant='h6'>{city.label}</Typography>
                </Box>
              ))}
            </Box>
            <Typography variant='body2' color='error'>
              <ErrorMessage
                name='applicantOnboarding.jobSearchPreferences.preferredLocation.cities'
                component={'span'}
              />
            </Typography>
          </Grid>
        )}

        {showProximitySearch && (
          <>
            <Grid item xs={12} md={12}>
              <Typography variant='h4' mb={4}>
                {t('applicantOnboarding.jobSearchPreferences.formLabels.proximitySearch')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={12} display={'flex'} gap={2}>
              <Grid item xs={12} md={6}>
                <Field
                  label={t('applicantOnboarding.jobSearchPreferences.formLabels.zipCode')}
                  required={false}
                  fullWidth
                  type='number'
                  component={EmpionTextField}
                  name='applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value'
                  value={formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value}
                  onChange={(e: any) => {
                    formik.setFieldValue(
                      'applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value',
                      e.target.value
                    );
                  }}
                />
                <Typography variant='body2' color='error'>
                  <ErrorMessage
                    name='applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value'
                    component={'span'}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  name='applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.radiusInKilometers'
                  label='Radius'
                  component={EmpionSelectField}
                  value={
                    formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch
                      .radiusInKilometers
                  }
                  disabled={
                    formik.values.applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value ===
                    ''
                  }
                  options={PROXIMITY_SEARCH_KM_OPTIONS.map((option) => ({
                    ...option,
                    label: option.label,
                  }))}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </OnboardingSection>
  );
}
